import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { motion } from 'motion/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError, NetworkError } from '@/shared/api/http';
import { signIn, useSession } from '@/shared/auth';
import { springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

import styles from './LoginPage.module.scss';

const schema = z.object({
  email: z.email(t.login.invalidEmail),
  password: z.string().min(8, t.login.passwordTooShort),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/public/login' });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await signIn(values.email, values.password);

      // Возврат туда, откуда человека увели на вход: он шёл по ссылке на
      // конкретный экран, и высаживать его на главную — терять его цель.
      // Только внутренние адреса: во внешнем можно увести на чужой сайт.
      const target = redirect?.startsWith('/') === true ? redirect : '/app';

      await navigate({ to: target });
    } catch (cause) {
      // Ошибка входа показывается у пароля, а не отдельным блоком: человек
      // смотрит туда, где последний раз печатал.
      const message =
        cause instanceof ApiError
          ? cause.detail
          : cause instanceof NetworkError
            ? t.common.networkError
            : t.common.unknownError;

      form.setError('password', { message });
    }
  });

  if (session.status === 'authenticated') {
    return (
      <div className={styles['card']}>
        <p className={styles['note']}>
          {t.login.alreadyIn} <b>{session.user.email}</b>.
        </p>
        <Button asChild iconRight={<ArrowRight />}>
          <Link to="/app">{t.login.goHome}</Link>
        </Button>
      </div>
    );
  }

  return (
    <motion.form
      className={styles['card']}
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={springs.spring}
    >
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.login.title}</h1>
        <p className={styles['subtitle']}>{t.login.subtitle}</p>
      </header>

      <Field label={t.login.email} required error={form.formState.errors.email?.message}>
        <Input
          type="email"
          autoComplete="username"
          placeholder="you@flow-do.ru"
          autoFocus
          {...form.register('email')}
        />
      </Field>

      <Field label={t.login.password} required error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="current-password" {...form.register('password')} />
      </Field>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={form.formState.isSubmitting}
        iconRight={<ArrowRight />}
      >
        {t.login.submit}
      </Button>

      <p className={styles['switch']}>
        {t.login.noAccount}{' '}
        <Link to="/register" className={styles['switch__link']}>
          {t.login.register}
        </Link>
        {' · '}
        <Link to="/forgot" className={styles['switch__link']}>
          {t.login.forgot}
        </Link>
      </p>
    </motion.form>
  );
}
